import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton,
  InputLabel, List, ListItemButton, ListItemText, MenuItem, Paper, Select, Stack, Switch, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as api from '../plannerApi';
import * as T from '../types';
import { Loader, NeedsHousehold, SectionTitle, useHousehold, useLoad, useNotify } from '../common';
import { dayName, mealText, people, toggleIn } from '../labels';
import { PLAN_PATH } from './shared';
import { MemberChips, useGoBack } from '../editing';

/** The defaults a pattern can have (anything else is chosen per meal). */
const KINDS: { value: T.MealSlotKind; text: string }[] = [
  { value: 'Empty', text: 'Por definir' },
  { value: 'Leftovers', text: 'Sobras da refeição anterior' },
  { value: 'Out', text: 'Fora' },
];

const patternTitle = (p: T.MealPattern) => `${dayName(p.dayOfWeek)} · ${mealText(p.mealType)}`;

function describe(p: T.MealPattern, members: T.HouseholdMember[]): string {
  const who = p.allMembers ? 'todos' : people(p.memberPublicIds, members);
  switch (p.defaultKind) {
    case 'Leftovers': return `${who} · sobras da refeição anterior`;
    case 'Out': return `${who} · fora`;
    default: return who;
  }
}

/** Edits one meal of the attendance pattern. */
function PatternDialog({ pattern, members, onClose }: { pattern: T.MealPattern; members: T.HouseholdMember[]; onClose: (saved: boolean) => void }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [all, setAll] = useState(pattern.allMembers);
  const [selected, setSelected] = useState<T.Guid[]>(pattern.allMembers ? members.map(m => m.publicId) : pattern.memberPublicIds);
  const [kind, setKind] = useState<T.MealSlotKind>(KINDS.some(k => k.value === pattern.defaultKind) ? pattern.defaultKind : 'Empty');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id: T.Guid) => setSelected(list => toggleIn(list, id));

  const save = async () => {
    setError(null);
    setBusy(true);
    try {
      await api.updateMealPatterns([{
        dayOfWeek: pattern.dayOfWeek,
        mealType: pattern.mealType,
        allMembers: all,
        memberPublicIds: all ? [] : members.filter(m => selected.includes(m.publicId)).map(m => m.publicId),
        defaultKind: kind,
      }]);
      onClose(true);
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => onClose(false)} fullScreen={isMobile} maxWidth="xs" fullWidth>
      <DialogTitle>{patternTitle(pattern)}</DialogTitle>
      <DialogContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="subtitle2">Todos</Typography>
            <Typography variant="caption" color="text.secondary">Inclui quem for adicionado à casa mais tarde.</Typography>
          </Box>
          <Switch checked={all} onChange={e => setAll(e.target.checked)} slotProps={{ input: { 'aria-label': 'Todos' } }} />
        </Stack>
        {!all && <Box sx={{ mt: 1 }}><MemberChips members={members} selected={selected} onToggle={toggle} /></Box>}

        <SectionTitle>Por defeito</SectionTitle>
        <FormControl fullWidth size="small">
          <InputLabel id="pattern-kind">Por defeito</InputLabel>
          <Select labelId="pattern-kind" label="Por defeito" value={kind} onChange={e => setKind(e.target.value as T.MealSlotKind)}>
            {KINDS.map(k => <MenuItem key={k.value} value={k.value}>{k.text}</MenuItem>)}
          </Select>
        </FormControl>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy} startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PatternsContent() {
  const goBack = useGoBack(PLAN_PATH);
  const notify = useNotify();
  const { members } = useHousehold();
  const { data, error, loading, reload } = useLoad(api.getMealPatterns);
  const [editing, setEditing] = useState<T.MealPattern | null>(null);

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton aria-label="Voltar" edge="start" onClick={goBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>Quem come</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        O habitual de cada refeição. Vale para as semanas que ainda não foram abertas; cada refeição pode depois ser alterada.
      </Typography>
      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {data && (
          <Paper variant="outlined">
            <List disablePadding>
              {data.map((p, i) => (
                <ListItemButton key={`${p.dayOfWeek}-${p.mealType}`} divider={i < data.length - 1} onClick={() => setEditing(p)}>
                  <ListItemText
                    primary={patternTitle(p)}
                    secondary={describe(p, members)}
                    slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Loader>
      {editing && (
        <PatternDialog
          pattern={editing}
          members={members}
          onClose={saved => {
            setEditing(null);
            if (saved) {
              notify('Padrão guardado.', { severity: 'success' });
              reload();
            }
          }}
        />
      )}
    </Box>
  );
}

/** Who usually eats each meal of the week, and whether it defaults to leftovers or eating out. */
const MealPatternsPage: React.FC = () => <NeedsHousehold><PatternsContent /></NeedsHousehold>;

export default MealPatternsPage;
